import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Mail, AlertCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("Demande envoyée !");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-foreground/40 hover:text-primary transition-colors font-semibold uppercase tracking-widest text-xs"
            data-testid="back-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </button>
          <span className="text-2xl font-bold text-primary">Sequenti</span>
        </div>

        <Card className="p-12 border-none bg-white shadow-xl rounded-lg border border-border/40 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-accent" />
          
          {!submitted ? (
            <>
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/5 rounded-full mb-6">
                  <Mail className="w-10 h-10 text-primary" />
                </div>
                <h1 className="text-4xl font-black text-primary mb-3">
                  Mot de passe oublié
                </h1>
                <p className="text-lg text-foreground/40 font-medium">
                  Saisissez votre email pour recevoir un lien de réinitialisation
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-destructive/5 text-destructive rounded-lg border border-destructive/10 text-base font-semibold">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                  </div>
                )}

                <div className="space-y-3">
                  <Label htmlFor="email" className="text-base font-bold text-primary">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                    data-testid="forgot-input-email"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-white hover:bg-primary/90 text-xl h-16 rounded-lg shadow-lg transition-all active:scale-95 mt-4"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? "Envoi en cours..." : "Envoyer le lien"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-50 rounded-full mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-black text-primary mb-4">
                Vérifiez vos emails
              </h2>
              <p className="text-lg text-foreground/60 mb-8">
                Si un compte existe pour <strong>{email}</strong>, vous recevrez un lien pour réinitialiser votre mot de passe sous peu.
              </p>
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary/5 text-lg h-14 rounded-lg"
              >
                Retour à la connexion
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
